import React from 'react';
import PropTypes from 'prop-types';
import ClipLoader from 'react-spinners/ClipLoader';
import Button from '../../../Button';
import styles from './StudentImport.module.sass';
import { fieldLabel } from '../../../../utils/headerMatch';
import { buildTeacherFindings } from '../../../../utils/studentImportTeacherFindings';
import { buildSeriousWarnings, ordinaryFindings } from '../../../../utils/studentImportSeriousWarnings';
import SeriousWarning from './SeriousWarning';

// unenrollment_candidates can run to dozens of names (it's the real list,
// not a pre-sampled few like the year-over-year side's sample_new/
// sample_missing) -- cap what's rendered so the screen can't overflow, and
// say so rather than silently dropping the rest.
const MAX_UNENROLL_SAMPLE = 15;

const findingShape = PropTypes.shape({
  severity: PropTypes.string,
  message: PropTypes.string,
});

const totalsShape = PropTypes.shape({
  prior: PropTypes.number,
  incoming: PropTypes.number,
  file: PropTypes.number,
  delta: PropTypes.number,
  pct_change: PropTypes.number,
});

const gradeShape = PropTypes.shape({
  grade: PropTypes.string,
  label: PropTypes.string,
  prior: PropTypes.number,
  incoming: PropTypes.number,
  delta: PropTypes.number,
  pct_change: PropTypes.number,
  severity: PropTypes.string,
});

const sampleShape = PropTypes.shape({
  student_id: PropTypes.string,
  name: PropTypes.string,
  grade: PropTypes.string,
});

const idsShape = PropTypes.shape({
  matched: PropTypes.number,
  new: PropTypes.number,
  new_in_entry_grades: PropTypes.number,
  missing: PropTypes.number,
  returning_match_pct: PropTypes.number,
  entry_grades: PropTypes.arrayOf(PropTypes.string),
  sample_new: PropTypes.arrayOf(sampleShape),
  sample_missing: PropTypes.arrayOf(sampleShape),
});

const promotionShape = PropTypes.shape({
  comparable: PropTypes.number,
  advanced_pct: PropTypes.number,
  repeated_pct: PropTypes.number,
  other: PropTypes.number,
  other_pct: PropTypes.number,
  sample_other: PropTypes.arrayOf(sampleShape),
});

// Row-by-row "will this create/update a student, match/create a teacher,
// who'd be unenrolled" preview -- StudentImportService::RowLevelPreview,
// GET .../row_level_preview. See docs/student_roster_import.md section 2.2.
const rowPreviewShape = PropTypes.shape({
  available: PropTypes.bool,
  error: PropTypes.string,
  creates: PropTypes.number,
  updates: PropTypes.number,
  invalid: PropTypes.number,
  truncated: PropTypes.bool,
  total_rows: PropTypes.number,
  // Only its length is read here (for the truncated note); per-row detail
  // isn't rendered on this screen.
  rows: PropTypes.arrayOf(PropTypes.shape({ row_number: PropTypes.number })),
  new_educator_names: PropTypes.arrayOf(PropTypes.string),
  unenrollment_candidates: PropTypes.arrayOf(PropTypes.shape({
    student_id: PropTypes.string,
    name: PropTypes.string,
  })),
});

// Companion staff/teacher file cross-check + LLM placeholder-name pass --
// StudentImportService::PairedFileCrossCheck / StaffNameAnomalyReviewer,
// GET .../paired_review. nil for an unpaired (single-file) import. See
// docs/student_roster_import.md section 2.1.
const pairedReviewShape = PropTypes.shape({
  available: PropTypes.bool,
  error: PropTypes.string,
  cross_check: PropTypes.shape({
    teachers_missing_from_staff_file: PropTypes.arrayOf(PropTypes.string),
    classroom_teachers: PropTypes.arrayOf(PropTypes.string),
    non_teaching_staff: PropTypes.arrayOf(PropTypes.string),
  }),
  anomalies: PropTypes.shape({
    ok: PropTypes.bool,
    error: PropTypes.string,
    flagged: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, reason: PropTypes.string })),
  }),
});

const SEVERITY_CLASS = {
  alert: styles.severityAlert,
  warn: styles.severityWarn,
  new: styles.severityNew,
  info: styles.severityInfo,
  ok: styles.severityOk,
};

const formatSeason = (season) => (season ? season.replace(/^([a-z]+)/i, (m) => m.charAt(0).toUpperCase() + m.slice(1)) : '');

const formatDelta = (delta, pct) => {
  if (delta === 0) return 'no change';
  const sign = delta > 0 ? '+' : '';
  return pct === null || pct === undefined ? `${sign}${delta}` : `${sign}${delta} (${sign}${pct}%)`;
};

const Findings = ({ findings }) => {
  if (!findings || findings.length === 0) {
    return (
      <p className={`${styles.finding} ${styles.severityOk}`}>
        Nothing looks out of place next to last season.
      </p>
    );
  }

  return (
    <ul className={styles.findingList}>
      {findings.map((finding) => (
        <li key={finding.message} className={`${styles.finding} ${SEVERITY_CLASS[finding.severity] || ''}`}>
          {finding.message}
        </li>
      ))}
    </ul>
  );
};

Findings.propTypes = { findings: PropTypes.arrayOf(findingShape) };
Findings.defaultProps = { findings: [] };

const TotalCard = ({ label, priorSeason, season, totals }) => (
  <div className={styles.statCard}>
    <div className={styles.statLabel}>{label}</div>
    <div className={styles.statNumbers}>
      <span className={styles.statPrior}>
        {totals.prior}
        <small>{formatSeason(priorSeason)}</small>
      </span>
      <span className={styles.statArrow}>&rarr;</span>
      <span className={styles.statIncoming}>
        {totals.incoming}
        <small>{formatSeason(season)}</small>
      </span>
    </div>
    <div className={styles.statDelta}>{formatDelta(totals.delta, totals.pct_change)}</div>
  </div>
);

TotalCard.propTypes = {
  label: PropTypes.string.isRequired,
  priorSeason: PropTypes.string,
  season: PropTypes.string,
  totals: totalsShape.isRequired,
};
TotalCard.defaultProps = { priorSeason: '', season: '' };

const GradeTable = ({ grades, priorSeason, season }) => {
  if (!grades || grades.length === 0) return null;

  return (
    <table className={styles.compareTable}>
      <thead>
        <tr>
          <th>Grade</th>
          <th>{formatSeason(priorSeason)}</th>
          <th>{formatSeason(season)}</th>
          <th>Change</th>
        </tr>
      </thead>
      <tbody>
        {grades.map((row) => (
          <tr key={row.grade} className={SEVERITY_CLASS[row.severity] || ''}>
            <td>{row.label}</td>
            <td>{row.prior}</td>
            <td>{row.incoming}</td>
            <td>{formatDelta(row.delta, row.pct_change)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

GradeTable.propTypes = {
  grades: PropTypes.arrayOf(gradeShape),
  priorSeason: PropTypes.string,
  season: PropTypes.string,
};
GradeTable.defaultProps = { grades: [], priorSeason: '', season: '' };

const SampleList = ({ title, rows }) => {
  if (!rows || rows.length === 0) return null;

  return (
    <div className={styles.sampleList}>
      <strong>{title}</strong>
      <ul>
        {rows.map((row) => (
          <li key={row.student_id}>
            {row.student_id}
            {row.name ? ` — ${row.name}` : ''}
            {row.grade ? ` (${row.grade})` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
};

SampleList.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(sampleShape),
};
SampleList.defaultProps = { rows: [] };

// What the FILE says its columns are, against what the mapping claimed.
//
// A difference is REFERRED, never resolved: nothing here rewrites the mapping,
// and the operator goes back a step to fix it. Silence is the good case, so the
// block renders nothing at all when the two agree.
const columnEntryShape = PropTypes.shape({
  field: PropTypes.string,
  header: PropTypes.string,
  content: PropTypes.string,
});

const columnsShape = PropTypes.shape({
  conflicts: PropTypes.arrayOf(columnEntryShape),
  suggestions: PropTypes.arrayOf(columnEntryShape),
  agreed: PropTypes.number,
  unavailable: PropTypes.bool,
});

const ColumnCheck = ({ columns, staffFile }) => {
  if (!columns || columns.unavailable) return null;

  const conflicts = columns.conflicts || [];
  const suggestions = columns.suggestions || [];
  if (conflicts.length === 0 && suggestions.length === 0) return null;

  return (
    <div className={styles.columnCheck}>
      {conflicts.length > 0 && (
        <>
          <div className={styles.columnCheckHead}>Columns to check</div>
          <ul>
            {conflicts.map((c) => (
              <li key={c.field}>
                {`${fieldLabel(c.field, { staffFile })} is mapped to `}
                <code>{c.header}</code>
                {', but the data in '}
                <code>{c.content}</code>
                {` looks like ${fieldLabel(c.field, { staffFile }).toLowerCase()}.`}
              </li>
            ))}
          </ul>
        </>
      )}

      {suggestions.length > 0 && (
        <>
          <div className={styles.columnCheckHead}>Columns you may have missed</div>
          <ul>
            {suggestions.map((s) => (
              <li key={s.field}>
                <code>{s.content}</code>
                {` looks like ${fieldLabel(s.field, { staffFile })}, and nothing is mapped to it.`}
              </li>
            ))}
          </ul>
        </>
      )}

      <p className={styles.columnCheckNote}>
        Nothing has been changed. Go back to the header step to fix a mapping, or continue if it is right.
      </p>
    </div>
  );
};

ColumnCheck.propTypes = { columns: columnsShape, staffFile: PropTypes.bool };
ColumnCheck.defaultProps = { columns: null, staffFile: false };

const IdSummary = ({ ids, priorSeason }) => {
  if (!ids) return null;

  return (
    <div className={styles.idSummary}>
      <div className={styles.idRow}>
        <span><strong>{ids.matched}</strong> returning (ID also in {formatSeason(priorSeason)})</span>
        <span><strong>{ids.new}</strong> new IDs — {ids.new_in_entry_grades} in {ids.entry_grades.join(' / ')}</span>
        <span><strong>{ids.missing}</strong> from last season are not in this file</span>
      </div>

      {ids.returning_match_pct !== null && ids.returning_match_pct !== undefined && (
        <p className={styles.idMatchRate}>
          {ids.returning_match_pct}% of the students who should be returning match an ID from {formatSeason(priorSeason)}.
        </p>
      )}

      <div className={styles.sampleColumns}>
        <SampleList title="Sample of new IDs" rows={ids.sample_new} />
        <SampleList title="Sample not in this file" rows={ids.sample_missing} />
      </div>
    </div>
  );
};

IdSummary.propTypes = {
  ids: idsShape,
  priorSeason: PropTypes.string,
};
IdSummary.defaultProps = { ids: null, priorSeason: '' };

const Promotion = ({ promotion, priorSeason }) => {
  if (!promotion) return null;

  return (
    <>
      <p className={styles.promotion}>
        Of {promotion.comparable} returning students with a numeric grade,{' '}
        <strong>{promotion.advanced_pct}%</strong> moved up exactly one grade and{' '}
        <strong>{promotion.repeated_pct}%</strong> are in the same grade as {formatSeason(priorSeason)}.
      </p>

      {/* Named students, because the count alone cannot tell a scrambled file
          from a school that genuinely reorganised -- the operator recognises
          the wrong pairing on sight. The service sends this ONLY when the alert
          fires, so the threshold lives in one place; an empty list here means
          the moves were within normal range. */}
      {promotion.sample_other && promotion.sample_other.length > 0 && (
        <SampleList title="Sample of other grade moves" rows={promotion.sample_other} />
      )}
    </>
  );
};

Promotion.propTypes = {
  promotion: promotionShape,
  priorSeason: PropTypes.string,
};
Promotion.defaultProps = { promotion: null, priorSeason: '' };

// "This file" card, alongside the year-over-year Students/Staff cards in the
// same stat row -- creates/updates answers a different question (what will
// THIS RUN do) from prior-vs-incoming totals, but an operator reading three
// cards left-to-right should not have to learn a second visual language for
// the third one.
const RowActionSummary = ({ rowPreview }) => {
  if (!rowPreview || !rowPreview.available) return null;

  return (
    <div className={styles.statCard}>
      <div className={styles.statLabel}>This file</div>
      <div className={styles.statNumbers}>
        <span className={styles.statIncoming}>
          {rowPreview.creates}
          <small>create</small>
        </span>
        <span className={styles.statArrow}>+</span>
        <span className={styles.statIncoming}>
          {rowPreview.updates}
          <small>update</small>
        </span>
      </div>
      {rowPreview.invalid > 0 && (
        <div className={styles.statDelta}>
          {rowPreview.invalid}
          {' row(s) have errors and will be skipped'}
        </div>
      )}
    </div>
  );
};

RowActionSummary.propTypes = { rowPreview: rowPreviewShape };
RowActionSummary.defaultProps = { rowPreview: null };

// Same finding/severity visual language as the year-over-year Findings list
// above, fed by a synthesis of the row-level preview and paired-file review
// instead of PriorSeasonComparison -- see studentImportTeacherFindings.js
// for why that synthesis lives in its own pure, testable function rather
// than inline here or duplicated server-side.
const TeacherFindings = ({ rowPreview, pairedReview }) => {
  const items = buildTeacherFindings({ rowPreview, pairedReview });
  if (items.length === 0) return null;

  return (
    <ul className={styles.findingList}>
      {items.map((item) => (
        <li key={item.key} className={`${styles.finding} ${SEVERITY_CLASS[item.severity] || ''}`}>
          {item.message}
        </li>
      ))}
    </ul>
  );
};

TeacherFindings.propTypes = { rowPreview: rowPreviewShape, pairedReview: pairedReviewShape };
TeacherFindings.defaultProps = { rowPreview: null, pairedReview: null };

// Extends StudentImport#overwrite_warning's count-only message (shown one
// screen later, once the operator has actually chosen to unenroll) with the
// real list computed here, read-only, before any decision is made --
// StudentImport#unenrollment_candidates is the exact exclusion query the
// real should_unenroll pass runs, so this can never disagree with what
// choosing "yes" on that later screen would actually do.
const UnenrollPreview = ({ rowPreview }) => {
  if (!rowPreview || !rowPreview.available || rowPreview.truncated) return null;

  const { unenrollment_candidates: candidates } = rowPreview;
  if (!candidates || candidates.length === 0) return null;

  const shown = candidates.slice(0, MAX_UNENROLL_SAMPLE);
  const remaining = candidates.length - shown.length;

  return (
    <div className={styles.standaloneSampleList}>
      <SampleList
        title={`Would be unenrolled if you choose to on a later screen (${candidates.length})`}
        rows={shown}
      />
      {remaining > 0 && (
        <p className={styles.previewNote}>
          {'…and '}
          {remaining}
          {' more.'}
        </p>
      )}
    </div>
  );
};

UnenrollPreview.propTypes = { rowPreview: rowPreviewShape };
UnenrollPreview.defaultProps = { rowPreview: null };

// The yellow block for a file nothing could vouch for: a school with no prior
// roster has no year-over-year check at all, and on this screen an absent
// finding otherwise reads as "looks fine". WHEN it applies and what it says are
// decided server-side (PriorSeasonComparison#caution) -- this only draws it.
const cautionShape = PropTypes.shape({
  title: PropTypes.string,
  message: PropTypes.string,
});

const Caution = ({ caution }) => {
  if (!caution) return null;

  return (
    <div className={styles.caution} role="alert">
      <p className={styles.cautionTitle}>{caution.title}</p>
      <p className={styles.cautionMessage}>{caution.message}</p>
    </div>
  );
};

Caution.propTypes = { caution: cautionShape };
Caution.defaultProps = { caution: null };

const ImportPreview = ({
  comparison, loading, error, rowPreview, pairedReview, staffFile, blockers, onCancel, onContinue,
}) => {
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <p>Checking this file before you import it…</p>
        <ClipLoader color="blue" loading size={120} />
      </div>
    );
  }

  const yearOverYearUnavailable = error || !comparison || comparison.available === false;
  // Not on `error`: a comparison that failed to build is a different problem
  // with its own note below, and says nothing about whether the school is new.
  const caution = (!error && comparison && comparison.caution) || null;
  const serious = buildSeriousWarnings({ blockers, comparison: yearOverYearUnavailable ? null : comparison });
  const blocked = serious.some((item) => item.blocking);
  const remainingFindings = yearOverYearUnavailable ? [] : ordinaryFindings(comparison.findings);
  // Red outranks yellow: a new school's file can also be mostly another
  // school's students. "This looks right" claims a check that did not happen.
  let continueLabel = 'This looks right — continue';
  if (serious.length > 0) continueLabel = 'I have read the warning — continue';
  else if (caution) continueLabel = 'Proceed';

  return (
    <div className={styles.preview}>
      <h3 className={styles.previewTitle}>
        {caution
          ? 'Review this file before importing'
          : `Check this file against ${formatSeason(comparison && comparison.prior_season)}`}
      </h3>

      {/* Before everything else, and nowhere else: the serious findings are
          lifted OUT of the lists below rather than repeated, so the page says
          each thing once, in the place it cannot be missed. */}
      <SeriousWarning
        title={blocked ? 'Stop: this file should not be imported as it is' : 'Read this before you continue'}
        items={serious}
        note={blocked ? 'The import will not run unless you confirm on the next screen that this file is right.' : null}
      />

      <Caution caution={caution} />

      {/* Order is deliberate, and reads top-to-bottom as one story instead of
          three: "what will THIS RUN do" (row-level + paired-file) leads,
          because it's true whether or not there is a prior season to compare
          against -- exactly the case (a brand-new school) where it matters
          most. The column check and year-over-year story follow, and can
          legitimately have nothing to say without hiding everything above
          them, unlike the old three-separate-screens flow. */}
      <div className={styles.statRow}>
        <RowActionSummary rowPreview={rowPreview} />

        {!yearOverYearUnavailable && comparison.totals.students && (
          <TotalCard
            label="Students"
            totals={comparison.totals.students}
            priorSeason={comparison.prior_season}
            season={comparison.season}
          />
        )}
        {!yearOverYearUnavailable && comparison.totals.staff && (
          <TotalCard
            label="Staff"
            totals={comparison.totals.staff}
            priorSeason={comparison.prior_season}
            season={comparison.season}
          />
        )}
      </div>

      {rowPreview && rowPreview.available === false && (
        <p className={styles.previewNote}>{rowPreview.error || 'Could not build the row-level preview.'}</p>
      )}

      {pairedReview && pairedReview.available === false && (
        <p className={styles.previewNote}>{pairedReview.error || 'Could not build the paired-file review.'}</p>
      )}

      <TeacherFindings rowPreview={rowPreview} pairedReview={pairedReview} />

      {rowPreview && rowPreview.truncated && (
        <p className={styles.previewNote}>
          {'This file has '}
          {rowPreview.total_rows}
          {' rows — only the first '}
          {rowPreview.rows ? rowPreview.rows.length : 0}
          {' were previewed in detail.'}
        </p>
      )}

      <UnenrollPreview rowPreview={rowPreview} />

      {/* Computed by the controller regardless of comparison.available (see
          docs/student_roster_import.md section 4) -- a school with no prior
          season is exactly the one whose columns nobody has checked before,
          so this must survive the year-over-year early return, not sit
          behind it. */}
      <ColumnCheck columns={comparison && comparison.columns} staffFile={staffFile} />

      {/* With a caution block above, the grey note would only repeat it in
          smaller type. */}
      {yearOverYearUnavailable ? !caution && (
        <p className={styles.previewNote}>
          {error || (comparison && comparison.baseline_note)
            || 'There is no previous season to compare this file against.'}
        </p>
      ) : (
        <>
          <p className={styles.previewNote}>{comparison.baseline_note}</p>

          {/* Every finding was an alert and moved to the red box: say nothing
              here, rather than Findings' "Nothing looks out of place". */}
          {(remainingFindings.length > 0 || serious.length === 0) && (
            <Findings findings={remainingFindings} />
          )}

          <Promotion promotion={comparison.promotion} priorSeason={comparison.prior_season} />

          <IdSummary ids={comparison.ids} priorSeason={comparison.prior_season} />

          <GradeTable
            grades={comparison.grades}
            priorSeason={comparison.prior_season}
            season={comparison.season}
          />
        </>
      )}

      <div className={styles.buttonContainer}>
        <button type="button" className={`btn btn-default ${styles.btn}`} onClick={onCancel}>
          {caution ? 'Cancel' : 'Cancel — do not import'}
        </button>

        {/* "This looks right" claims a check that did not happen. */}
        <Button type="button" variant="primary" onClick={onContinue}>
          {continueLabel}
        </Button>
      </div>
    </div>
  );
};

ImportPreview.propTypes = {
  comparison: PropTypes.shape({
    available: PropTypes.bool,
    season: PropTypes.string,
    prior_season: PropTypes.string,
    baseline_note: PropTypes.string,
    findings: PropTypes.arrayOf(findingShape),
    totals: PropTypes.shape({ students: totalsShape, staff: totalsShape }),
    grades: PropTypes.arrayOf(gradeShape),
    ids: idsShape,
    promotion: promotionShape,
    columns: columnsShape,
    caution: cautionShape,
  }),
  loading: PropTypes.bool,
  error: PropTypes.string,
  rowPreview: rowPreviewShape,
  pairedReview: pairedReviewShape,
  staffFile: PropTypes.bool,
  blockers: PropTypes.arrayOf(PropTypes.string),
  onCancel: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
};

ImportPreview.defaultProps = {
  comparison: null,
  loading: false,
  error: null,
  rowPreview: null,
  pairedReview: null,
  staffFile: false,
  blockers: [],
};

export default ImportPreview;
